import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function TDEECalculator() {
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [activity, setActivity] = useState("1.55");
  const [results, setResults] = useState<any>(null);

  const calculate = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);
    const activityLevel = parseFloat(activity);
    
    if (!w || !h || !a || w <= 0 || h <= 0 || a <= 0) return;
    
    let bmr: number;
    if (gender === "male") {
      bmr = 10 * w + 6.25 * h - 5 * a + 5;
    } else {
      bmr = 10 * w + 6.25 * h - 5 * a - 161;
    }
    
    const tdee = bmr * activityLevel;
    
    setResults({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      cutting: Math.round(tdee - 500),
      bulking: Math.round(tdee + 500)
    });
  };

  return (
    <PageLayout calculatorName="TDEE Calculator" calculatorPath="/calculator/tdee">
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate TDEE</CardTitle>
            <CardDescription>Total Daily Energy Expenditure</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Gender</Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Weight (kg)</Label>
              <Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="70" />
            </div>
            <div>
              <Label>Height (cm)</Label>
              <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="175" />
            </div>
            <div>
              <Label>Age (years)</Label>
              <Input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="25" />
            </div>
            <div>
              <Label>Activity Level</Label>
              <Select value={activity} onValueChange={setActivity}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1.2">Sedentary (little/no exercise)</SelectItem>
                  <SelectItem value="1.375">Lightly active (1-3 days/week)</SelectItem>
                  <SelectItem value="1.55">Moderately active (3-5 days/week)</SelectItem>
                  <SelectItem value="1.725">Very active (6-7 days/week)</SelectItem>
                  <SelectItem value="1.9">Extra active (athlete)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={calculate} className="w-full">Calculate</Button>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Your TDEE</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center mb-4">
                  <div className="text-5xl font-bold text-primary mb-2">{results.tdee}</div>
                  <div className="text-lg text-muted-foreground">calories/day</div>
                </div>
                <div className="space-y-2 border-t pt-4">
                  <div className="flex justify-between">
                    <span>BMR (at rest):</span>
                    <span className="font-semibold">{results.bmr} cal</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cutting (weight loss):</span>
                    <span className="font-semibold text-red-500">{results.cutting} cal</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bulking (weight gain):</span>
                    <span className="font-semibold text-green-500">{results.bulking} cal</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="md:col-span-2">
              <CalculatorSuperCalcCTA config={calculatorCTAs["tdee"]} trigger={results.tdee} />
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
}
